"use client";

import { useQuery } from "@tanstack/react-query";

import type { SessionSupport } from "@/lib/support-for-session";

import { SupportSurface } from "./support-surface";

/**
 * `SupportForSession` for a page rendered before anyone was reading it.
 *
 * The expert docs are static, so the server cannot know who is reading them:
 * whether anybody is signed in, and which surface their rollout gives them, is
 * asked once the page has loaded, through the same decision the layouts make.
 * Until it answers, and for a signed-out reader, the corner stays empty.
 */
export function DeferredSupportForSession() {
  const { data } = useQuery({
    queryKey: ["support-surface"],
    queryFn: async (): Promise<SessionSupport | null> => {
      const response = await fetch("/api/support-surface");
      if (!response.ok) {
        throw new Error(`support surface fetch failed: ${response.status}`);
      }
      const body = (await response.json()) as {
        support: SessionSupport | null;
      };
      return body.support;
    },
    // Who is reading does not change while they read; a sign-in is a full
    // page load that asks again.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    // A failed read leaves the corner as a signed-out reader sees it, and the
    // docs themselves are unaffected, so there is nothing worth retrying for.
    retry: false,
  });

  return data ? (
    <SupportSurface shouldUseExpertAgent={data.shouldUseExpertAgent} />
  ) : null;
}
